import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { CacheProvider } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { renderedLanguage } from '../i18n/documentLanguage';
import { createAppTheme } from './index';
import { createNoncedEmotionCache, readCspNonce } from './cspNonce';
// Types for components.MuiDataGrid in the theme (REV-803).
import type {} from '@mui/x-data-grid/themeAugmentation';

export type ThemeModePreference = 'light' | 'dark' | 'system';
export type ResolvedMode = 'light' | 'dark';

const STORAGE_KEY = 'theme-mode';

interface ThemeModeContextValue {
  /** What the user chose. */
  preference: ThemeModePreference;
  /** What that resolves to right now — 'system' follows the OS. */
  resolved: ResolvedMode;
  setPreference: (mode: ThemeModePreference) => void;
  /** True when the OS asks for less motion, or the user opted in per-account. */
  reducedMotion: boolean;
  setReducedMotion: (on: boolean) => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

export function useThemeMode(): ThemeModeContextValue {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) {
    throw new Error('useThemeMode must be used inside ThemeModeProvider');
  }
  return ctx;
}

function readStored<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const value = localStorage.getItem(key);
    return value && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
  } catch {
    // Private-mode / disabled storage must not take the app down.
    return fallback;
  }
}

function prefersDark(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false;
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
}

/**
 * REV-491: applies the user's appearance preferences.
 *
 * Dark mode has been shipped in this codebase once before, as a switch that
 * stored a value and changed nothing, and REV-445 deleted it. So this provider
 * does the part that actually matters: it swaps the MUI theme, and there are
 * tests asserting the rendered palette changes rather than that a boolean was
 * persisted.
 *
 * 'system' is the default and genuinely follows the OS — it subscribes to the
 * media query, so flipping the OS theme updates the app live rather than only
 * on reload.
 *
 * Preferences are mirrored to localStorage so the choice survives a reload
 * BEFORE the API responds; the account-level copy in User.preferences is the
 * source of truth and is synced by the Appearance settings tab. Without the
 * local mirror every page load would flash the wrong theme while /users/me was
 * in flight.
 */
export default function ThemeModeProvider({ children }: { children: React.ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemeModePreference>(() =>
    readStored(STORAGE_KEY, ['light', 'dark', 'system'] as const, 'system')
  );
  const [systemDark, setSystemDark] = useState(prefersDark);

  const [reducedMotionPref, setReducedMotionState] = useState<'system' | 'reduce'>(() =>
    readStored('reduced-motion', ['system', 'reduce'] as const, 'system')
  );
  const [systemReducedMotion, setSystemReducedMotion] = useState(prefersReducedMotion);

  // Follow the OS live. Without this, 'system' would only be correct until the
  // user changed their OS theme, and then silently wrong until a reload.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const dark = window.matchMedia('(prefers-color-scheme: dark)');
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onDark = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    const onMotion = (e: MediaQueryListEvent) => setSystemReducedMotion(e.matches);
    dark.addEventListener('change', onDark);
    motion.addEventListener('change', onMotion);
    return () => {
      dark.removeEventListener('change', onDark);
      motion.removeEventListener('change', onMotion);
    };
  }, []);

  const setPreference = useCallback((mode: ThemeModePreference) => {
    setPreferenceState(mode);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* storage unavailable — the in-memory choice still applies */
    }
  }, []);

  const setReducedMotion = useCallback((on: boolean) => {
    const value = on ? 'reduce' : 'system';
    setReducedMotionState(value);
    try {
      localStorage.setItem('reduced-motion', value);
    } catch {
      /* as above */
    }
  }, []);

  const resolved: ResolvedMode =
    preference === 'system' ? (systemDark ? 'dark' : 'light') : preference;
  // The OS setting can only ever ADD reduction — a per-account "off" must not
  // override someone's system-level request for less motion.
  const reducedMotion = reducedMotionPref === 'reduce' || systemReducedMotion;

  // REV-803: the per-response CSP nonce, fixed for the life of the page.
  // REV-796: the theme's direction follows the rendered language, matching <html dir>.
  const { i18n } = useTranslation();
  const direction = i18n.dir(renderedLanguage(i18n));

  const [cspNonce] = useState(readCspNonce);

  const theme = useMemo(() => {
    const created = createAppTheme(resolved, direction);
    // REV-803: MUI X DataGrid renders its own <style> for its CSS variables,
    // outside emotion, and stamps it with its `nonce` prop. Set it here once
    // for every grid rather than at each call site.
    const base = {
      ...created,
      components: {
        ...created.components,
        MuiDataGrid: {
          ...created.components?.MuiDataGrid,
          defaultProps: { ...created.components?.MuiDataGrid?.defaultProps, nonce: cspNonce },
        },
      },
    } as typeof created;
    if (!reducedMotion) return base;
    return {
      ...base,
      transitions: { ...base.transitions, create: () => 'none' },
    } as typeof base;
  }, [resolved, direction, reducedMotion, cspNonce]);

  const value = useMemo(
    () => ({ preference, resolved, setPreference, reducedMotion, setReducedMotion }),
    [preference, resolved, setPreference, reducedMotion, setReducedMotion]
  );

  // REV-803: MUI renders through this cache so every <style> it injects
  // carries the per-response CSP nonce. Created once, like the nonce.
  const [emotionCache] = useState(createNoncedEmotionCache);

  return (
    <ThemeModeContext.Provider value={value}>
      <CacheProvider value={emotionCache}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {children}
        </ThemeProvider>
      </CacheProvider>
    </ThemeModeContext.Provider>
  );
}
